import type { Bundle, BundleNode, FieldType, InputField, Property } from './types';
import { isDecisionNode, isManagedNode, isObjectType, isRulesNode, isTaskNode } from './types';

export type FieldKeyError = 'empty' | 'invalid' | 'duplicate';

// Keep in sync with the backend `FieldKey` parser in sop_types/src/ids.rs.
const FIELD_KEY_PATTERN = /^[A-Za-z_][A-Za-z0-9_]*$/;

export const isBlankKey = (key: string): boolean => key.trim().length === 0;

export const DEFAULT_FIELD_KEY = 'new_field';
export const DEFAULT_QUESTION_KEY = 'new_question';
export const DEFAULT_PROPERTY_KEY = 'new_property';
export const DEFAULT_ARTIFACT_NAME = 'New artifact';
const MAX_ARTIFACT_KEY_LENGTH = 64;

// Also skip CEL output keys; the validator only checks artifact-vs-artifact.
export const takenOutputKeys = (bundle: Pick<Bundle, 'nodes'>): Set<string> => {
  const keys = new Set<string>();
  for (const node of bundle.nodes) {
    if (isTaskNode(node)) {
      for (const field of node.fields) keys.add(field.name);
      for (const artifact of node.artifacts) keys.add(artifact.id);
    } else if (isRulesNode(node)) {
      keys.add(node.key);
    } else if (isDecisionNode(node)) {
      for (const question of node.questions) keys.add(question.key);
    }
  }
  return keys;
};

const slugify = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

// Identity, minted once. Re-deriving on rename orphans Percy's pending ops.
export const uniqueArtifactKey = (name: string, taken: Set<string>): string => {
  let base = slugify(name);
  if (!base) base = 'artifact';
  // FieldKey rejects a leading digit; "2024 Summary" would 400 the node's PATCH.
  if (!/^[A-Za-z_]/.test(base)) base = `a_${base}`;
  if (base.length > MAX_ARTIFACT_KEY_LENGTH) {
    base = base.slice(0, MAX_ARTIFACT_KEY_LENGTH).replace(/_+$/g, '');
  }
  if (!taken.has(base)) return base;
  let n = 2;
  let candidate = `${base}_${n}`;
  while (taken.has(candidate)) {
    n += 1;
    candidate = `${base}_${n}`;
  }
  return candidate;
};

export const uniqueArtifactKeys = (names: string[], taken: Set<string>): string[] => {
  const used = new Set(taken);
  return names.map(name => {
    const key = uniqueArtifactKey(name, used);
    used.add(key);
    return key;
  });
};

export const fieldKeyError = (key: string): FieldKeyError | undefined => {
  if (isBlankKey(key)) return 'empty';
  if (!FIELD_KEY_PATTERN.test(key)) return 'invalid';
  return undefined;
};

type NamedField = { id: string; name: string };

export const namedFieldKeyError = (field: NamedField, siblings: NamedField[]): FieldKeyError | undefined => {
  const formatError = fieldKeyError(field.name);
  if (formatError) return formatError;
  // Flag only the later occurrence as the duplicate.
  const index = siblings.findIndex(sibling => sibling.id === field.id);
  const isDuplicate = siblings.some((other, i) => i < index && other.name === field.name);
  return isDuplicate ? 'duplicate' : undefined;
};

// Recurse into a field's child properties, but only for the types that carry them.
export const childrenKeyError = (field: {
  type: FieldType | null;
  children?: Property[];
}): FieldKeyError | undefined => (isObjectType(field.type) ? propertiesKeyError(field.children ?? []) : undefined);

const propertiesKeyError = (properties: Property[]): FieldKeyError | undefined => {
  for (const property of properties) {
    const error = namedFieldKeyError(property, properties) ?? childrenKeyError(property);
    if (error) return error;
  }
  return undefined;
};

// Validates all agent inputs.
export const inputsKeyError = (inputs: InputField[]): FieldKeyError | undefined => {
  for (const input of inputs) {
    const error = namedFieldKeyError(input, inputs) ?? childrenKeyError(input);
    if (error) return error;
  }
  return undefined;
};

// Validate a rules output key, every decision question key, or all task fields/properties.
export const nodeKeyError = (node: BundleNode): FieldKeyError | undefined => {
  // Managed nodes have no user-editable keys — their outputs are version-owned.
  if (isManagedNode(node)) return undefined;
  if (isDecisionNode(node)) return decisionKeyError(node.questions);
  if (!isTaskNode(node)) return fieldKeyError(node.key);

  for (const field of node.fields) {
    const error = namedFieldKeyError(field, node.fields) ?? childrenKeyError(field);
    if (error) return error;
  }
  return undefined;
};

export const questionKeyError = (
  question: { id: string; key: string },
  siblings: { id: string; key: string }[],
): FieldKeyError | undefined =>
  namedFieldKeyError(
    { id: question.id, name: question.key },
    siblings.map(sibling => ({ id: sibling.id, name: sibling.key })),
  );

const decisionKeyError = (questions: { id: string; key: string }[]): FieldKeyError | undefined => {
  for (const question of questions) {
    const error = questionKeyError(question, questions);
    if (error) return error;
  }
  return undefined;
};
